import { create } from "zustand";
import { api, ChartOut, HITLInitResponse, JobListItem, JobOut, JobPageOut, openJobSocket } from "../api/client";

export interface AiIntakeProgress {
  page: number;
  total: number;
  chartsFound: number;
  message: string;
  classifyModel: string;
  semanticsModel: string;
  phase: "classify" | "semantics" | "done" | "paused" | "error";
  /** Last N log lines from the AI worker */
  log: string[];
  /** Section currently being semanticized */
  currentSection?: string;
  semanticsCurrent?: number;
  semanticsTotal?: number;
}

interface JobState {
  // Admin dashboard list
  jobs: JobListItem[];
  jobsLoading: boolean;

  // Selected job detail
  selectedJob: JobOut | null;
  charts: ChartOut[];
  chartsLoading: boolean;

  // Selected chart for HITL
  selectedChart: ChartOut | null;
  hitlInit: HITLInitResponse | null;
  hitlLoading: boolean;

  // Live WebSocket event log for the selected job
  liveEvents: Array<Record<string, unknown>>;

  // Intake scanning progress (null = not scanning) — rule-based
  intakeProgress: { scanned: number; total: number; chartsFound: number } | null;

  // AI intake progress (null = not running)
  aiIntakeProgress: AiIntakeProgress | null;

  // All pages from intake scan (album view)
  pages: JobPageOut[];
  pagesLoading: boolean;

  // Categorize result
  categorizeTree: Record<string, Record<string, number[]>> | null;
  categorizeFolders: string[] | null;
  categorizing: boolean;

  // WebSocket cleanup ref
  _wsCleanup: (() => void) | null;
}

interface JobActions {
  loadJobs(): Promise<void>;
  uploadJob(file: File, onProgress?: (pct: number) => void): Promise<void>;
  selectJob(jobId: string): Promise<void>;
  deleteJob(jobId: string): Promise<void>;
  selectChart(chart: ChartOut): Promise<void>;
  approveChart(jobId: string, chartId: string): Promise<void>;
  flagChart(jobId: string, chartId: string, reason: string): Promise<void>;
  clearHITL(): void;
  togglePage(jobId: string, pageNum: number): Promise<void>;
  categorizeJob(jobId: string): Promise<void>;
  pauseAiIntake(jobId: string): Promise<void>;
  resumeAiIntake(jobId: string): Promise<void>;
  _handleWsEvent(data: Record<string, unknown>): void;
}

export const useJobStore = create<JobState & JobActions>((set, get) => ({
  // ── initial state ──────────────────────────────────────────────────────
  jobs: [],
  jobsLoading: false,
  selectedJob: null,
  charts: [],
  chartsLoading: false,
  selectedChart: null,
  hitlInit: null,
  hitlLoading: false,
  liveEvents: [],
  intakeProgress: null,
  aiIntakeProgress: null,
  pages: [],
  pagesLoading: false,
  categorizeTree: null,
  categorizeFolders: null,
  categorizing: false,
  _wsCleanup: null,

  // ── actions ────────────────────────────────────────────────────────────

  async loadJobs() {
    set({ jobsLoading: true });
    try {
      const jobs = await api.jobs.list();
      const { selectedJob } = get();
      const updatedSelected = selectedJob
        ? jobs.find((j) => j.id === selectedJob.id) ?? null
        : null;
      set({
        jobs,
        jobsLoading: false,
        ...(updatedSelected && updatedSelected.status !== selectedJob?.status
          ? { selectedJob: { ...selectedJob!, status: updatedSelected.status } }
          : {}),
      });
    } catch {
      set({ jobsLoading: false });
    }
  },

  async uploadJob(file, onProgress) {
    const job = await api.jobs.upload(file, onProgress);
    if (!job?.id) throw new Error("Server did not return a valid job ID");
    const listItem: JobListItem = {
      id: String(job.id),
      source_filename: job.source_filename,
      status: job.status,
      pending_hitl_count: job.pending_hitl_count,
      chart_count: job.charts?.length ?? 0,
      assigned_operator: job.assigned_operator,
      created_at: job.created_at,
      updated_at: job.updated_at,
    };
    set((s) => ({ jobs: [listItem, ...s.jobs] }));
    await get().selectJob(String(job.id));
  },

  async selectJob(jobId) {
    // Tear down previous WebSocket
    get()._wsCleanup?.();

    set({ chartsLoading: true, selectedJob: null, charts: [], liveEvents: [], intakeProgress: null, aiIntakeProgress: null, pages: [], categorizeTree: null, categorizeFolders: null });
    try {
      const [job, charts, pages] = await Promise.all([
        api.jobs.get(jobId),
        api.jobs.charts(jobId),
        api.jobs.pages(jobId),
      ]);
      set({ selectedJob: job, charts, pages, chartsLoading: false });

      // Open live WebSocket for this job
      const cleanup = openJobSocket(jobId, (data) => get()._handleWsEvent(data));
      set({ _wsCleanup: cleanup });
    } catch {
      set({ chartsLoading: false });
    }
  },

  async deleteJob(jobId) {
    await api.jobs.delete(jobId);
    const { selectedJob, _wsCleanup } = get();
    if (selectedJob?.id === jobId) {
      _wsCleanup?.();
      set({ selectedJob: null, charts: [], selectedChart: null, hitlInit: null, _wsCleanup: null });
    }
    set((s) => ({ jobs: s.jobs.filter((j) => j.id !== jobId) }));
  },

  async selectChart(chart) {
    const canOpenWPD = ["NEEDS_REVIEW", "COMPLETED", "AUTO_APPROVED"].includes(chart.status);
    if (!canOpenWPD) {
      set({ selectedChart: chart, hitlInit: null });
      return;
    }

    set({ selectedChart: chart, hitlLoading: true, hitlInit: null });
    try {
      const init = await api.hitl.init(chart.job_id, chart.id);
      set({ hitlInit: init, hitlLoading: false });
    } catch {
      set({ hitlLoading: false });
    }
  },

  async approveChart(jobId, chartId) {
    await api.hitl.approve(jobId, chartId);
    // Refresh chart list
    const charts = await api.jobs.charts(jobId);
    set({ charts, hitlInit: null, selectedChart: null });
  },

  async flagChart(jobId, chartId, reason) {
    await api.hitl.flag(jobId, chartId, reason);
    const charts = await api.jobs.charts(jobId);
    set({ charts, hitlInit: null, selectedChart: null });
  },

  clearHITL() {
    set({ selectedChart: null, hitlInit: null });
  },

  async togglePage(jobId, pageNum) {
    const updated = await api.jobs.togglePage(jobId, pageNum);
    set((s) => ({
      pages: s.pages.map((p) => p.page_num === pageNum ? updated : p),
    }));
  },

  async categorizeJob(jobId) {
    set({ categorizing: true });
    try {
      const result = await api.jobs.categorize(jobId);
      const job = await api.jobs.get(jobId);
      set({ categorizeTree: result.tree, categorizeFolders: result.batch_folders ?? null, selectedJob: job, categorizing: false });
    } catch {
      set({ categorizing: false });
    }
  },

  async pauseAiIntake(jobId) {
    await api.intakeAi.pause(jobId);
  },

  async resumeAiIntake(jobId) {
    await api.intakeAi.resume(jobId);
    const job = await api.jobs.get(jobId);
    set({ selectedJob: job });
  },

  _handleWsEvent(data) {
    set((s) => ({ liveEvents: [data, ...s.liveEvents].slice(0, 100) }));

    const event = data.event as string | undefined;

    // ── Rule-based intake events ───────────────────────────────────────────
    if (event === "INTAKE_PROGRESS") {
      set({
        intakeProgress: {
          scanned: data.scanned as number,
          total: data.total as number,
          chartsFound: data.charts_found as number,
        },
      });
      return;
    }
    if (event === "INTAKE_DONE") {
      set({ intakeProgress: null });
      const jobId = get().selectedJob?.id as string | undefined;
      if (jobId) {
        Promise.all([api.jobs.pages(jobId), api.jobs.get(jobId)])
          .then(([pages, job]) => set({ pages, selectedJob: job }))
          .catch(() => {});
      }
      return;
    }

    // ── AI intake events ──────────────────────────────────────────────────
    if (event === "INTAKE_AI_START") {
      set({
        aiIntakeProgress: {
          page: 0,
          total: data.total as number,
          chartsFound: 0,
          message: data.message as string ?? "",
          classifyModel: data.classify_model as string ?? "",
          semanticsModel: data.semantics_model as string ?? "",
          phase: "classify",
          log: [],
        },
      });
      return;
    }
    if (event === "INTAKE_AI_PROGRESS") {
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          page: data.page as number,
          total: data.total as number,
          chartsFound: data.charts_found as number,
          message: data.message as string ?? s.aiIntakeProgress.message,
          phase: "classify",
        } : null,
      }));
      return;
    }
    if (event === "INTAKE_AI_LOG") {
      const msg = data.message as string ?? "";
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          message: msg,
          log: [msg, ...s.aiIntakeProgress.log].slice(0, 50),
        } : null,
      }));
      return;
    }
    if (event === "INTAKE_AI_PASS1_DONE") {
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          phase: "semantics",
          chartsFound: data.charts_found as number,
          message: data.message as string ?? "",
        } : null,
      }));
      return;
    }
    if (event === "INTAKE_AI_SEMANTICS") {
      const msg = data.message as string ?? "";
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          phase: "semantics",
          message: msg,
          currentSection: data.section as string,
          semanticsCurrent: data.current as number,
          semanticsTotal: data.total as number,
          log: [msg, ...s.aiIntakeProgress.log].slice(0, 50),
        } : null,
      }));
      return;
    }
    if (event === "INTAKE_AI_PAUSED") {
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          phase: "paused",
          message: data.message as string ?? "پاوز شد",
        } : null,
      }));
      const jobId = get().selectedJob?.id as string;
      if (jobId) api.jobs.get(jobId).then((job) => set({ selectedJob: job })).catch(() => {});
      return;
    }
    if (event === "INTAKE_AI_ERROR") {
      const msg = data.message as string ?? "خطای ناشناخته";
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          phase: "error",
          message: msg,
          log: [msg, ...s.aiIntakeProgress.log].slice(0, 50),
        } : null,
      }));
      return;
    }
    if (event === "INTAKE_AI_DONE") {
      set((s) => ({
        aiIntakeProgress: s.aiIntakeProgress ? {
          ...s.aiIntakeProgress,
          phase: "done",
          message: data.message as string ?? "کامل شد",
        } : null,
      }));
      const jobId = get().selectedJob?.id as string;
      if (jobId) api.jobs.get(jobId).then((job) => set({ selectedJob: job })).catch(() => {});
      return;
    }

    // Legacy — kept for compatibility
    if (event === "JOB_STARTED") {
      set({ intakeProgress: null });
      return;
    }

    // Refresh chart list on any chart-level state change
    if (
      event &&
      ["HITL_REQUIRED", "CHART_AUTO_APPROVED", "CHART_COMPLETED",
       "CHART_APPROVED", "CHART_CORRECTIONS_SUBMITTED", "CHART_FLAGGED",
       "PANELS_DETECTED", "CHART_SKIPPED", "JOB_COMPLETED"].includes(event)
    ) {
      const jobId = (data.job_id ?? get().selectedJob?.id) as string | undefined;
      if (jobId) {
        api.jobs.charts(jobId)
          .then((charts) => set({ charts }))
          .catch(() => {});
      }
    }
  },
}));
